// ─── ClinicalVerificationPanel ──────────────────────────────────────────
// Étape 3 du chantier blocages (2026-07-31). Panneau praticienne de la
// section « Santé et suivi médical ».
//
// RÔLE : donner à Anissa la main sur les champs cliniques STRUCTURÉS qui
// conditionneront les sept blocages, et lui permettre de reprendre les
// dossiers existants — ceux remplis avant l'existence de ces questions.
//
// CE QU'IL NE FAIT PAS :
//  - il ne bloque rien (les blocages arrivent à l'étape suivante) ;
//  - il ne déduit aucun diagnostic : le texte libre ne fait qu'afficher une
//    demande de vérification, jamais une conclusion ;
//  - il ne lève aucun blocage. La relecture est scellée et horodatée par le
//    clic final de sauvegarde d'Anissa, dans le formulaire parent.
//
// CONFIDENTIALITÉ : ces données restent côté praticienne. Aucun nom de
// médicament ni posologie n'est affiché dans l'application cliente
// (consigne Anissa, 2026-07-31).

import {
  DIABETE_OPTIONS,
  DIGESTIF_OPTIONS,
  MICI_STATUT_OPTIONS,
  THYROIDE_OPTIONS,
  THYROIDE_STATUT_OPTIONS,
  TRAITEMENTS_OPTIONS,
  basculerValeurMultiple,
} from '../services/clinical/clinicalFieldOptions';
import {
  normaliserSelectionDigestive,
  contientMici,
  ensembleMiciChange,
  poserStatutMici,
  CHAMP_LIAISON_STATUT,
} from '../services/clinical/digestifSelection';
import {
  resolveClinicalState,
  messageVerificationClinique,
  noteFinale,
  THYRO_A_STABILISER,
} from '../services/clinical/resolveClinicalState';
import { OUVERTURE_COMPLEMENTS } from '../services/prompts/nutrition/anamneseFoundation';
import {
  NO_FOOD_RESTRICTION,
  RESTRICTIONS_MAP,
} from '../services/prompts/nutrition/restrictions.fr';
import { SmartTextarea } from '../KeywordHints';
import {
  exactLegacyTreatmentText,
  reconcileExplicitTreatmentChoice,
} from '../services/clinical/anamnesisReview';

const OUVERTURE_COMPLEMENTS_OPTIONS = Object.freeze(
  Object.entries(OUVERTURE_COMPLEMENTS).map(([value, label]) => Object.freeze({ value, label })),
);

const RESTRICTION_OPTIONS = Object.freeze([
  Object.freeze({ value: NO_FOOD_RESTRICTION, label: 'Aucune restriction alimentaire' }),
  ...Object.entries(RESTRICTIONS_MAP).map(([value, item]) => Object.freeze({
    value,
    label: item.label,
  })),
]);

/**
 * Cases à cocher, pour un champ à réponses multiples (arbitrage 4 d'Anissa,
 * 2026-08-01 : un SII et un RGO peuvent coexister).
 *
 * « Aucun diagnostic » est exclusif à la saisie : le cocher efface les
 * autres, et cocher un diagnostic l'efface. Rien n'est déduit, la
 * praticienne voit exactement ce qu'elle a coché.
 */
export function resolveLegacyMultipleConfirmation(value, options) {
  if (Array.isArray(value) || typeof value !== 'string' || !value.trim()) return null;
  const option = options.find((candidate) => candidate.value === value.trim());
  return option
    ? Object.freeze({ recognized: true, value: option.value, label: option.label, next: [option.value] })
    : Object.freeze({ recognized: false, value: value.trim(), label: value.trim(), next: null });
}

function ChampCasesMultiples({ label, name, options, value, onChange, aide }) {
  const valeurs = Array.isArray(value) ? value : [];
  const legacy = resolveLegacyMultipleConfirmation(value, options);
  const coche = (v) => valeurs.includes(v);
  const basculer = (v) => onChange(name, basculerValeurMultiple(value, v));

  return (
    <div className="field full-width">
      <label>{label}</label>
      <div style={casesStyle}>
        {options.map((o) => (
          <label key={o.value} style={caseStyle}>
            <input
              type="checkbox"
              checked={coche(o.value)}
              onChange={() => basculer(o.value)}
            />
            <span>{o.label}</span>
          </label>
        ))}
      </div>
      {legacy && (
        <div role="status" style={legacyMultipleStyle}>
          <strong>Ancienne réponse à confirmer : {legacy.label}</strong>
          <span>
            Cette valeur n’est pas encore validée dans le format structuré actuel.
          </span>
          {legacy.recognized ? (
            <button
              type="button"
              className="btn btn-anissa-secondary"
              onClick={() => onChange(name, legacy.next)}
            >
              Confirmer cette réponse
            </button>
          ) : (
            <span>Choisissez une réponse reconnue dans la liste ci-dessus.</span>
          )}
        </div>
      )}
      {aide && <small style={aideStyle}>{aide}</small>}
    </div>
  );
}

/** Select « - » + options. Le vide reste distinct d'« Aucun diagnostic ». */
function ChampSelect({ label, name, options, value, onChange, aide }) {
  const id = `clinical-${name}`;
  return (
    <div className="field full-width">
      <label htmlFor={id}>{label}</label>
      <select id={id} value={value || ''} onChange={(e) => onChange(name, e.target.value)}>
        <option value="">— Non renseigné —</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      {aide && <small style={aideStyle}>{aide}</small>}
    </div>
  );
}

function ChampBooleanExplicit({ label, name, value, onChange, aide }) {
  const id = `clinical-${name}`;
  const selected = typeof value === 'boolean' ? String(value) : '';
  return (
    <div className="field full-width">
      <label htmlFor={id}>{label}</label>
      <select
        id={id}
        value={selected}
        onChange={(event) => {
          if (event.target.value === '') return onChange(name, '');
          return onChange(name, event.target.value === 'true');
        }}
      >
        <option value="">— Non renseigné —</option>
        <option value="false">Non</option>
        <option value="true">Oui</option>
      </select>
      {aide && <small style={aideStyle}>{aide}</small>}
    </div>
  );
}

export function resolveRestrictionToggle(value, autre, code) {
  const selection = Array.isArray(value) ? value : [];
  if (code === NO_FOOD_RESTRICTION) {
    const selectingNone = !selection.includes(code);
    return {
      restrictionsAlimentaires: selectingNone ? [code] : [],
      restrictionsAutre: selectingNone ? '' : String(autre || ''),
    };
  }
  const sansAucune = selection.filter((item) => item !== NO_FOOD_RESTRICTION);
  const removing = sansAucune.includes(code);
  return {
    restrictionsAlimentaires: removing
      ? sansAucune.filter((item) => item !== code)
      : [...sansAucune, code],
    restrictionsAutre: code === 'autre' && removing ? '' : String(autre || ''),
  };
}

function RestrictionsAlimentairesField({ value, autre, onChange }) {
  const selection = Array.isArray(value) ? value : [];
  const toggle = (code) => {
    const next = resolveRestrictionToggle(selection, autre, code);
    onChange('restrictionsAlimentaires', next.restrictionsAlimentaires);
    if (next.restrictionsAutre !== String(autre || '')) {
      onChange('restrictionsAutre', next.restrictionsAutre);
    }
  };
  return (
    <div className="field full-width">
      <label>Restrictions alimentaires permanentes</label>
      <div style={casesStyle}>
        {RESTRICTION_OPTIONS.map((option) => (
          <label key={option.value} style={caseStyle}>
            <input
              type="checkbox"
              checked={selection.includes(option.value)}
              onChange={() => toggle(option.value)}
            />
            <span>{option.label}</span>
          </label>
        ))}
      </div>
      <small style={aideStyle}>
        Confirmez « aucune » ou chaque restriction déclarée. Le Ramadan est traité séparément.
      </small>
      {selection.includes('autre') && (
        <div style={{ marginTop: 10 }}>
          <label>Précisez l’autre restriction</label>
          <SmartTextarea
            value={autre || ''}
            onChange={(event) => onChange('restrictionsAutre', event.target.value)}
            rows={2}
          />
        </div>
      )}
    </div>
  );
}

function RamadanInactiveConfirmation({ value, onChange }) {
  const id = 'clinical-ramadan-actif';
  return (
    <div className="field full-width">
      <label htmlFor={id}>Adaptation Ramadan pour ce programme</label>
      <select
        id={id}
        value={typeof value === 'boolean' ? String(value) : ''}
        onChange={(event) => {
          if (event.target.value === '') onChange('ramadanActif', '');
          if (event.target.value === 'false') onChange('ramadanActif', false);
        }}
      >
        <option value="">— Non renseigné —</option>
        <option value="false">Aucune adaptation Ramadan active</option>
        {value === true && (
          <option value="true" disabled>Adaptation active — configuration existante en lecture seule</option>
        )}
      </select>
      <small style={aideStyle}>
        Cette fiche permet uniquement de confirmer l’absence d’adaptation. Elle n’active jamais automatiquement un mode Ramadan.
      </small>
    </div>
  );
}

function choixComplementsActuels(value) {
  if (!Array.isArray(value) || value.length === 0) return '';
  const item = value[0];
  if (
    value.length === 1
    && item
    && typeof item === 'object'
    && !Array.isArray(item)
    && String(item.nom || '').trim().toLowerCase() === 'aucun'
    && !String(item.dose || '').trim()
  ) {
    return 'aucun';
  }
  return 'liste';
}

function ComplementsActuelsField({ value, onChange }) {
  const choix = choixComplementsActuels(value);
  const lignes = choix === 'liste' && Array.isArray(value) ? value : [];

  function changerChoix(next) {
    if (next === 'aucun') {
      onChange('complementsActuels', [{ nom: 'Aucun', dose: '' }]);
      return;
    }
    if (next === 'liste') {
      onChange(
        'complementsActuels',
        choix === 'liste' && lignes.length > 0 ? lignes : [{ nom: '', dose: '' }],
      );
      return;
    }
    onChange('complementsActuels', []);
  }

  function changerLigne(index, field, next) {
    onChange('complementsActuels', lignes.map((item, itemIndex) => {
      const ligne = item && typeof item === 'object' && !Array.isArray(item) ? item : {};
      const normalisee = {
        nom: String(ligne.nom || ''),
        dose: String(ligne.dose || ''),
      };
      return itemIndex === index ? { ...normalisee, [field]: next } : normalisee;
    }));
  }

  function supprimerLigne(index) {
    onChange('complementsActuels', lignes.filter((_, itemIndex) => itemIndex !== index));
  }

  return (
    <div className="field full-width">
      <label htmlFor="clinical-complements-actuels">Compléments alimentaires actuellement pris</label>
      <select
        id="clinical-complements-actuels"
        value={choix}
        onChange={(event) => changerChoix(event.target.value)}
      >
        <option value="">— Non renseigné —</option>
        <option value="aucun">Aucun complément actuellement</option>
        <option value="liste">Un ou plusieurs compléments</option>
      </select>
      <small style={aideStyle}>
        Déclarez uniquement ce que la cliente prend déjà. Aucune suggestion n’est créée ici.
      </small>

      {choix === 'liste' && (
        <div style={complementsListeStyle}>
          {lignes.map((item, index) => (
            <div key={index} style={complementLigneStyle}>
              <div>
                <label htmlFor={`complement-nom-${index}`}>Nom du complément {index + 1}</label>
                <input
                  id={`complement-nom-${index}`}
                  type="text"
                  value={String(item?.nom || '')}
                  onChange={(event) => changerLigne(index, 'nom', event.target.value)}
                  placeholder="Ex. Vitamine D"
                />
              </div>
              <div>
                <label htmlFor={`complement-dose-${index}`}>Dose ou fréquence, si connue</label>
                <input
                  id={`complement-dose-${index}`}
                  type="text"
                  value={String(item?.dose || '')}
                  onChange={(event) => changerLigne(index, 'dose', event.target.value)}
                  placeholder="Recopier l’indication du produit, si connue"
                />
              </div>
              <button
                type="button"
                className="btn btn-anissa-secondary"
                onClick={() => supprimerLigne(index)}
              >
                Retirer
              </button>
            </div>
          ))}
          <button
            type="button"
            className="btn btn-anissa-secondary"
            onClick={() => onChange('complementsActuels', [...lignes, { nom: '', dose: '' }])}
          >
            Ajouter un complément
          </button>
        </div>
      )}
    </div>
  );
}

export default function ClinicalVerificationPanel({ form, updateField }) {
  const clinical = resolveClinicalState(form);
  const aVerifier = clinical.verificationRequise;
  const message = aVerifier.length ? messageVerificationClinique(aVerifier) : null;

  // Réponses multiples : une MICI déclarée EN PLUS d'un SII ouvre la
  // question du statut exactement comme une MICI déclarée seule.
  const miciOuvert = clinical.digestif.miciDeclaree;
  const thyroOuvert = THYRO_A_STABILISER.includes(clinical.thyroide.valeur);
  const traitementsOui = clinical.traitements.declares === 'oui';
  const legacy = exactLegacyTreatmentText(form);

  function majTraitements(_name, status) {
    const updates = reconcileExplicitTreatmentChoice(form, status);
    Object.entries(updates).forEach(([field, value]) => updateField(field, value));
  }

  // Le statut MICI n'a de sens que tant qu'une MICI est déclarée. Décocher
  // la dernière l'efface immédiatement : sans ça, un « en rémission » saisi
  // puis retiré survivrait dans la fiche et pourrait lever un blocage si
  // une MICI était recochée plus tard, sans avoir jamais été reconfirmé.
  // 2026-08-02 : le statut est aussi redemandé quand l'ENSEMBLE des MICI
  // change. Il répondait à une question qui n'est plus la même : un « en
  // rémission » saisi pour Crohn ne dit rien d'une seconde maladie ajoutée
  // ensuite, et le reconduire en silence levait le blocage pour les deux.
  function majDigestif(name, valeurs) {
    const selection = normaliserSelectionDigestive(valeurs);
    const aChange = ensembleMiciChange(form.digestifDiagnostic, selection);
    updateField(name, selection);
    if (form.miciStatut && (!contientMici(selection) || aChange)) {
      updateField('miciStatut', '');
    }
  }

  // Répondre au statut écrit AUSSI la liaison : à quel ensemble de MICI
  // cette réponse répond. Sans elle, le résolveur refuse le statut et
  // bloque — y compris une réponse qu'Anissa vient de saisir. C'est le prix
  // de la protection posée le 2026-08-02, et il se paie ici, une fois.
  function majStatutMici(name, valeur) {
    const avec = poserStatutMici(form, valeur);
    updateField(name, avec.miciStatut);
    updateField(CHAMP_LIAISON_STATUT, avec[CHAMP_LIAISON_STATUT]);
  }

  return (
    <div className="anamnese-subsection" style={{ marginTop: 32 }}>
      <h4>Santé et suivi médical</h4>

      {/* Encadré PRIORITAIRE (2026-07-31) : il passe avant tout le reste,
          y compris avant la demande de vérification. Chaque motif est listé
          séparément — trois situations bloquantes font trois lignes, pas un
          message fourre-tout. La vérification praticienne ne les lève pas. */}
      {clinical.motifs.length > 0 && (
        <div style={blocageStyle}>
          <div style={blocageTitreStyle}>Génération automatique bloquée</div>
          <ul style={blocageListeStyle}>
            {/* On affiche SON message, entier et tel quel. Le libellé court
                existe dans les données (journaux, suivi) mais pas ici : il
                répéterait la phrase par laquelle son message commence. */}
            {clinical.motifs.map((m) => (
              <li key={m.id} style={blocageItemStyle}>
                <span style={blocageMessageStyle}>{m.message}</span>
              </li>
            ))}
          </ul>
          {/* La note dépend du niveau réel : une mention détectée en texte
              libre peut être infirmée par la clarification, la génération
              redevient alors possible. Seule une situation structurée impose
              la construction manuelle. */}
          <p style={blocageNoteStyle}>
            {noteFinale(clinical.motifs)}
            {clinical.blocages.length > 0
              && ' La sauvegarde de l’anamnèse ne lève pas ces motifs.'}
          </p>
        </div>
      )}

      {message && (
        <div style={alerteStyle}>
          <div style={alerteTitreStyle}>{message.titre}</div>
          <p style={alerteCorpsStyle}>{message.corps}</p>
        </div>
      )}

      {/* Le signal familial — décision d'Anissa du 2026-08-03. Il INFORME et
          ne bloque rien : d'où un ton et une couleur qui ne ressemblent pas à
          ceux d'une alerte. Une note qui a l'air d'un blocage finit par être
          traitée comme un blocage. */}
      {clinical.antecedentsFamiliaux.present && (
        <div style={signalFamilialStyle}>
          <strong style={{ fontWeight: 600 }}>Antécédents familiaux déclarés</strong>
          {' — '}
          {clinical.antecedentsFamiliaux.domaines.map((d) => LIBELLE_DOMAINE[d] || d).join(', ')}.
          <div style={{ marginTop: 4, opacity: 0.85 }}>
            Information de contexte : elle ne bloque pas la génération et ne
            qualifie aucun diagnostic. Le détail est dans le champ « antécédents
            familiaux » de la fiche.
          </div>
        </div>
      )}

      <p style={introStyle}>
        Diagnostics posés par un professionnel de santé. Un champ laissé vide
        vaut « non renseigné » — ce n&apos;est pas la même chose qu&apos;« aucun
        diagnostic », qui est une réponse à part entière.
      </p>

      <div className="form-grid">
        <ChampSelect
          label="Diabète ou trouble de la régulation glycémique diagnostiqué"
          name="diabeteDiagnostic"
          options={DIABETE_OPTIONS}
          value={form.diabeteDiagnostic}
          onChange={updateField}
        />

        <ChampCasesMultiples
          label="Troubles digestifs diagnostiqués"
          name="digestifDiagnostic"
          options={DIGESTIF_OPTIONS}
          value={form.digestifDiagnostic}
          onChange={majDigestif}
          aide="Plusieurs réponses possibles : un SII et un RGO peuvent coexister."
        />
        {miciOuvert && (
          <ChampSelect
            label="État actuel de la MICI selon le suivi médical"
            name="miciStatut"
            options={MICI_STATUT_OPTIONS}
            value={form.miciStatut}
            onChange={majStatutMici}
          />
        )}

        <ChampSelect
          label="Trouble de la thyroïde diagnostiqué"
          name="thyroideDiagnostic"
          options={THYROIDE_OPTIONS}
          value={form.thyroideDiagnostic}
          onChange={updateField}
        />
        {thyroOuvert && (
          <ChampSelect
            label="Situation considérée comme stabilisée par le médecin"
            name="thyroideStatut"
            options={THYROIDE_STATUT_OPTIONS}
            value={form.thyroideStatut}
            onChange={updateField}
          />
        )}

        {legacy && (
          <div className="field full-width" role="status" style={legacyTreatmentStyle}>
            <strong>Information déjà présente dans l’ancien dossier</strong>
            <span style={{ whiteSpace: 'pre-wrap' }}>{legacy}</span>
            <small>
              Confirme « Oui » pour la recopier dans le champ structuré, ou
              « Non » pour indiquer qu’aucun traitement n’est actuellement pris.
            </small>
          </div>
        )}

        <ChampSelect
          label="Traitements médicamenteux en cours"
          name="traitementsActuels"
          options={TRAITEMENTS_OPTIONS}
          value={form.traitementsActuels}
          onChange={majTraitements}
        />
        {traitementsOui && (
          <>
            <div className="field full-width">
              <label>Nom exact de chaque traitement</label>
              <SmartTextarea
                value={form.traitementsDetail || ''}
                onChange={(e) => updateField('traitementsDetail', e.target.value)}
                placeholder="Un traitement par ligne"
                rows={3}
              />
            </div>
            <div className="field full-width">
              <label>Moment habituel de prise</label>
              <SmartTextarea
                value={form.traitementsMomentPrise || ''}
                onChange={(e) => updateField('traitementsMomentPrise', e.target.value)}
                placeholder="Le matin à jeun, le soir au coucher…"
                rows={2}
              />
            </div>
          </>
        )}

        <ComplementsActuelsField
          value={form.complementsActuels}
          onChange={updateField}
        />

        <ChampSelect
          label="Ouverture aux compléments alimentaires"
          name="ouvertureComplements"
          options={OUVERTURE_COMPLEMENTS_OPTIONS}
          value={form.ouvertureComplements}
          onChange={updateField}
          aide="Choisissez la préférence exprimée par la cliente. Ce choix ne valide ni ne déclenche automatiquement une recommandation."
        />

        <RestrictionsAlimentairesField
          value={form.restrictionsAlimentaires}
          autre={form.restrictionsAutre}
          onChange={updateField}
        />

        <RamadanInactiveConfirmation
          value={form.ramadanActif}
          onChange={updateField}
        />

        <ChampBooleanExplicit
          label="Présentation du programme sans chiffres"
          name="presentationSansChiffres"
          value={form.presentationSansChiffres}
          onChange={updateField}
          aide="Oui : portions en unités familières, sans grammes ni calories. Non : présentation habituelle."
        />

        {/* Champ historique. Conservé tant que la reprise des anciens
            dossiers n'est pas terminée : c'est là que vit l'information des
            clientes qui ont rempli le questionnaire avant les questions
            structurées. Il alimente encore la détection de
            contre-indications (ContraIndicationAlert). */}
        <div className="field full-width">
          <label>Traitements — information précédemment renseignée</label>
          <SmartTextarea
            value={form.traitements || ''}
            onChange={(e) => updateField('traitements', e.target.value)}
            placeholder="Champ historique, antérieur aux questions structurées"
            rows={2}
          />
          <small style={aideStyle}>
            {legacy
              ? 'Reportez cette information dans les champs structurés ci-dessus, puis sauvegardez l’anamnèse.'
              : 'Vide pour cette cliente.'}
          </small>
        </div>

        <small style={aideStyle}>
          La sauvegarde finale confirme que les informations structurées ci-dessus
          correspondent aux éléments déclarés dans le dossier. Cette vérification
          ne lève pas un éventuel blocage de sécurité.
        </small>
      </div>
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────

const introStyle = {
  margin: '2px 0 14px',
  fontSize: '.82rem',
  lineHeight: 1.5,
  opacity: 0.75,
};

const legacyMultipleStyle = {
  marginTop: 10,
  padding: '10px 12px',
  border: '1px solid rgba(196, 160, 80, .45)',
  borderRadius: 8,
  background: 'rgba(196, 160, 80, .10)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 7,
  fontSize: '.8rem',
};

const legacyTreatmentStyle = {
  marginTop: 4,
  padding: '12px 14px',
  border: '1px solid rgba(196, 160, 80, .45)',
  borderRadius: 8,
  background: 'rgba(196, 160, 80, .10)',
  display: 'flex',
  flexDirection: 'column',
  gap: 7,
  fontSize: '.8rem',
};

// Encadré de blocage : registre rouge, distinct du jaune de la simple
// demande de vérification. Un blocage n'est pas une alerte.
const blocageStyle = {
  background: 'rgba(220,80,80,.08)',
  border: '1px solid rgba(220,80,80,.40)',
  borderLeft: '3px solid rgba(220,80,80,.75)',
  borderRadius: 10,
  padding: '14px 16px',
  margin: '4px 0 16px',
};

const blocageTitreStyle = {
  fontSize: '.92rem',
  fontWeight: 700,
  color: '#7f1d1d',
};

const blocageListeStyle = {
  listStyle: 'none',
  margin: '10px 0 0',
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
};

const blocageItemStyle = {
  paddingLeft: 12,
  borderLeft: '1px solid rgba(220,80,80,.30)',
};

const blocageMessageStyle = {
  display: 'block',
  marginTop: 3,
  fontSize: '.78rem',
  lineHeight: 1.5,
  color: '#8f2f2f',
  opacity: 0.92,
};

const blocageNoteStyle = {
  margin: '12px 0 0',
  paddingTop: 9,
  borderTop: '1px solid rgba(220,80,80,.20)',
  fontSize: '.75rem',
  lineHeight: 1.5,
  color: '#7f3a3a',
};

const alerteStyle = {
  background: 'rgba(220,180,80,.08)',
  border: '1px solid rgba(220,180,80,.32)',
  borderRadius: 10,
  padding: '12px 16px',
  margin: '4px 0 16px',
};

/**
 * Le signal familial. Volontairement PLUS DISCRET que l'alerte ci-dessus :
 * un filet à gauche, pas de cadre, pas d'ambre. Il informe, il ne réclame
 * rien — et une note qui a l'air d'un blocage finit par être traitée comme un
 * blocage.
 */
const signalFamilialStyle = {
  borderLeft: '2px solid rgba(130,195,158,.5)',
  paddingLeft: 12,
  margin: '4px 0 16px',
  fontSize: '.8rem',
  color: '#304538',
  lineHeight: 1.55,
};

/** Les domaines, dits en français plutôt qu'en clés techniques. */
const LIBELLE_DOMAINE = {
  diabete: 'diabète',
  digestif: 'digestif',
  thyroide: 'thyroïde',
};

const alerteTitreStyle = {
  fontSize: '.86rem',
  fontWeight: 700,
  color: '#6f4f0d',
};

const alerteCorpsStyle = {
  margin: '6px 0 0',
  fontSize: '.8rem',
  lineHeight: 1.5,
  color: '#765b20',
  opacity: 0.9,
};

const aideStyle = {
  opacity: 0.7,
  display: 'block',
  marginTop: 4,
};

const caseStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontWeight: 400,
  cursor: 'pointer',
  marginTop: 4,
};

/** Grille des cases d'un champ à réponses multiples. */
const casesStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
  gap: '2px 16px',
  marginTop: 4,
};

const complementsListeStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  marginTop: 12,
};

const complementLigneStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))',
  alignItems: 'end',
  gap: 10,
};
